import React from "react";
import { colors } from "../../theme";
import { useStudio } from "../../store/useStudio";
import { PILLAR_BY_ID } from "../../data/matrices";
import { RECOVERY_METHODS, CONDENSER_TYPES, isCondenserLoop, thermalRecovery, TYPOLOGIES, organicKgPersonYr } from "../../lib/kbLogic";
import { OPERATING_PROFILES, defaultProfileFor, countryFromGeo, annualOperatingHours, profileBasis } from "../../lib/operatingProfile";
import { quantifyResource, DEFAULT_DIVERT_PCT } from "../../lib/resources";
import { getOptTip } from "../../data/ringGuide";
import { isEnergyResource } from "../EnergyProfilePanel";
import { styles } from "./tokens";
import { resourceIcon } from "./icons";
import { CiteTag, OptionRow } from "./primitives";
import { InfoTip } from "../InfoTip";

// Build the footprint tooltip: how the area scales, the vendor evidence behind the fit, and sources.
function areaTooltip(am) {
  if (!am) return "";
  const modelText = {
    power: "Power-law: larger single units are more area-efficient, so the footprint grows slower than throughput (economies of scale), with a fixed smallest-unit floor.",
    linear: "Linear biological loading: a fixed mass is processed per m² of bed, so area grows in direct proportion to diverted waste — no economy of scale.",
    modular: "Modular units: one more wall-standing unit is added per fixed throughput step.",
    geometric: "Geometric (tank): area grows with the daily batch/settling volume.",
    zero: "No added area — integrates into existing plant/ducts.",
  }[am.model] || "";
  const vend = (am.vendors || []).filter((v) => v.footprint_m2 != null || v.capacity != null).slice(0, 4)
    .map((v) => `${[v.vendor, v.model].filter(Boolean).join(" ")}: ${v.capacity != null ? `${v.capacity} ${v.capacity_unit || ""}` : ""}${v.footprint_m2 != null ? ` → ${v.footprint_m2} m²` : ""}`.trim());
  const refs = (am.refs || []).filter(Boolean);
  return [
    modelText,
    am.penalty_behaviour ? ` ${am.penalty_behaviour}` : "",
    vend.length ? ` — Vendor evidence: ${vend.join(" · ")}.` : "",
    refs.length ? ` Sources: ${refs.join("; ")}.` : "",
  ].filter(Boolean).join("");
}

// PHASE 4 — Resources are QUANTIFICATION-only (no equipment sizing). Show the available waste
// stream, the valorized output (with /year time-scale) + expected range, and — for renewables —
// a PV area input with a dynamic GHI-driven generation range and a manual waste-heat anchor.
// Thermal-exchange loop configuration (Ventilation Exhaust Air / Cooling System Condenser Heat).
// A heat exchanger transfers heat hot→cold; recovered kWh = available kW × effectiveness × annual
// operating hours (from a holiday-aware weekly profile). Ventilation methods that only preheat
// incoming outdoor air contribute 0 kWh to agriculture until a secondary export loop is added.
export function ThermalConfig({ resource }) {
  const geo = useStudio((s) => s.geo);
  const typologyId = useStudio((s) => s.buildingTypology);
  const cfg = useStudio((s) => (s.resourceInputs.thermal || {})[resource.id]) || {};
  const setResourceThermal = useStudio((s) => s.setResourceThermal);
  const openEnergyPanel = useStudio((s) => s.openEnergyPanel);
  const patch = (p) => setResourceThermal(resource.id, p);
  const country = countryFromGeo(geo);
  const condenser = isCondenserLoop(resource);
  const profileId = cfg.profileId || defaultProfileFor(typologyId);
  const custom = { wkd: cfg.customWkd, wke: cfg.customWke };
  const hasOverride = cfg.hoursOverride != null && cfg.hoursOverride !== "";
  const derivedHours = annualOperatingHours(profileId, country, custom);
  const hours = hasOverride ? Math.max(0, Math.min(8760, Number(cfg.hoursOverride) || 0)) : derivedHours;
  const tr = thermalRecovery(resource, { ...cfg, profileId, custom }, country, typologyId);
  const method = RECOVERY_METHODS.find((m) => m.id === (cfg.method || "plate")) || RECOVERY_METHODS[0];
  const ctype = CONDENSER_TYPES.find((c) => c.id === (cfg.condenserType || "air")) || CONDENSER_TYPES[0];

  return (
    <div style={styles.blockBox} data-testid={`thermal-config-${resource.id}`}>
      <div style={styles.thermalIntro}>Transfers heat from a hotter to a colder stream; it does not create heat.</div>

      {condenser ? (
        <>
          <div style={styles.thermalField}>
            <span style={styles.qLabel}>Condenser type</span>
            <div style={styles.segToggle}>
              {CONDENSER_TYPES.map((c) => (
                <button key={c.id} type="button" data-testid={`condenser-type-${c.id}-${resource.id}`}
                  onClick={(e) => { e.stopPropagation(); patch({ condenserType: c.id }); }}
                  style={{ ...styles.segBtn, ...(ctype.id === c.id ? styles.segBtnOn : {}) }}>{c.label}</button>
              ))}
            </div>
          </div>
          <div style={styles.thermalRow}>
            <span style={styles.qLabel}>Available condenser heat</span>
            <input type="number" min="0" step="1" value={cfg.availableKw ?? ""} placeholder="0" data-testid={`thermal-available-kw-${resource.id}`}
              onClick={(e) => e.stopPropagation()} onChange={(e) => patch({ availableKw: e.target.value })} style={styles.areaInput} />
            <span style={styles.areaUnit}>kW</span>
          </div>
          <div style={styles.thermalRow}>
            <span style={styles.qLabel}>Recovery effectiveness</span>
            <input type="number" min="0" max="100" step="1" value={cfg.recoveryEff ?? ""} placeholder={String(Math.round(ctype.recovery * 100))} data-testid={`thermal-recovery-eff-${resource.id}`}
              onClick={(e) => e.stopPropagation()} onChange={(e) => patch({ recoveryEff: e.target.value })} style={styles.areaInput} />
            <span style={styles.areaUnit}>%</span>
          </div>
          <div style={styles.thermalHint}>{ctype.note} Suggested {Math.round(ctype.recovery * 100)}%, editable.</div>
        </>
      ) : (
        <>
          <div style={styles.thermalRow}>
            <span style={styles.qLabel}>Available exhaust heat</span>
            <input type="number" min="0" step="1" value={cfg.availableKw ?? ""} placeholder="0" data-testid={`thermal-available-kw-${resource.id}`}
              onClick={(e) => e.stopPropagation()} onChange={(e) => patch({ availableKw: e.target.value })} style={styles.areaInput} />
            <span style={styles.areaUnit}>kW</span>
          </div>
          <div style={styles.thermalField}>
            <span style={styles.qLabel}>Recovery method</span>
            <select value={cfg.method || "plate"} data-testid={`thermal-method-select-${resource.id}`}
              onClick={(e) => e.stopPropagation()} onChange={(e) => patch({ method: e.target.value })} style={styles.thermalSelect}>
              {RECOVERY_METHODS.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
            </select>
          </div>
          <div style={styles.thermalRow}>
            <span style={styles.qLabel}>Sensible effectiveness</span>
            <input type="number" min="0" max="100" step="1" value={cfg.sensibleEff ?? ""} placeholder={String(Math.round(method.sensible * 100))} data-testid={`thermal-sensible-eff-${resource.id}`}
              onClick={(e) => e.stopPropagation()} onChange={(e) => patch({ sensibleEff: e.target.value })} style={styles.areaInput} />
            <span style={styles.areaUnit}>%</span>
          </div>
          {method.latent > 0 && <div style={styles.thermalHint}>Latent effectiveness ≈ {Math.round(method.latent * 100)}% (moisture transfer to incoming OA).</div>}
          <div style={styles.thermalHint}>Recovered heat goes to: <b>{method.dest}</b>. {method.note}</div>
          {!method.exportable && (
            <div style={styles.thermalWarn} data-testid={`thermal-export-note-${resource.id}`}>
              This method recovers to ventilation air only, contributing <b>0 kWh</b> to the agriculture loop. Add a secondary export loop (e.g. a run-around coil or exhaust-air heat pump) to supply agriculture.
            </div>
          )}
        </>
      )}

      <div style={styles.thermalField}>
        <span style={styles.qLabel}>Operating profile</span>
        <select value={profileId} data-testid={`thermal-profile-select-${resource.id}`}
          onClick={(e) => e.stopPropagation()} onChange={(e) => patch({ profileId: e.target.value })} style={styles.thermalSelect}>
          {Object.entries(OPERATING_PROFILES).map(([id, p]) => (<option key={id} value={id}>{p.label}</option>))}
        </select>
      </div>
      {profileId === "custom" && (
        <div style={styles.thermalRow}>
          <span style={styles.qLabel}>Weekday / weekend h</span>
          <input type="number" min="0" max="24" value={cfg.customWkd ?? ""} placeholder="8" data-testid={`thermal-custom-wkd-${resource.id}`}
            onClick={(e) => e.stopPropagation()} onChange={(e) => patch({ customWkd: e.target.value })} style={styles.areaInput} />
          <input type="number" min="0" max="24" value={cfg.customWke ?? ""} placeholder="0" data-testid={`thermal-custom-wke-${resource.id}`}
            onClick={(e) => e.stopPropagation()} onChange={(e) => patch({ customWke: e.target.value })} style={styles.areaInput} />
        </div>
      )}
      <div style={styles.thermalRow}>
        <span style={styles.qLabel}>Annual operating hours</span>
        <input type="number" min="0" max="8760" value={cfg.hoursOverride ?? ""} placeholder={String(derivedHours)} data-testid={`thermal-hours-${resource.id}`}
          onClick={(e) => e.stopPropagation()} onChange={(e) => patch({ hoursOverride: e.target.value })} style={styles.areaInput} />
        <span style={styles.areaUnit}>h/yr</span>
      </div>
      <div style={styles.thermalHint}>Derived {derivedHours.toLocaleString()} h/yr from {profileBasis(profileId, country, custom)}. Override if you have metered run-hours.</div>

      <div style={styles.qRow}>
        <span style={styles.qLabel}>{tr.exportable ? "Recovered to agriculture" : "Recovered to ventilation preheat"}</span>
        <span style={{ ...styles.qVal, color: tr.recoveredGrossKwh > 0 ? colors.ok : colors.textTertiary }} data-testid={`thermal-recovered-${resource.id}`}>
          {tr.recoveredGrossKwh.toLocaleString()} kWh/yr
        </span>
      </div>
      {!tr.exportable && (
        <div style={styles.qRow}>
          <span style={{ ...styles.qLabel, color: colors.textTertiary }}>Exportable to agriculture</span>
          <span style={{ ...styles.qVal, color: colors.textTertiary }} data-testid={`thermal-exportable-${resource.id}`}>0 kWh/yr</span>
        </div>
      )}
      <div style={styles.qRange}>= {tr.kw || 0} kW × {Math.round(tr.eff * 100)}% × {hours.toLocaleString()} h/yr = {tr.recoveredGrossKwh.toLocaleString()} kWh/yr{tr.exportable ? " to agriculture" : " → incoming ventilation air (not exportable to agriculture)"}</div>

      {isEnergyResource(resource.id) && (
        <button style={styles.profileBtn} data-testid={`energy-profile-open-${resource.id}`}
          onClick={(e) => { e.stopPropagation(); openEnergyPanel(resource.id); }}>
          View operational profile (day/night · seasons)
        </button>
      )}

      {condenser && (
        <div style={styles.thermalConsult} data-testid={`thermal-consult-${resource.id}`}>
          Consult your mechanical engineer to confirm condenser type, recoverable capacity, and operating schedule before relying on these figures.
        </div>
      )}
    </div>
  );
}

export function ResourceQuantify({ resource }) {
  const occupancy = useStudio((s) => s.occupancy);
  const weather = useStudio((s) => s.weather);
  const kb = useStudio((s) => s.kb);
  const pvAreaM2 = useStudio((s) => s.resourceInputs.pvAreaM2);
  const solarThermalAreaM2 = useStudio((s) => s.resourceInputs.solarThermalAreaM2);
  const solarThermalEff = useStudio((s) => s.resourceInputs.solarThermalEff);
  const divertMap = useStudio((s) => s.resourceInputs.divert);
  const setResourceInput = useStudio((s) => s.setResourceInput);
  const setResourceDivert = useStudio((s) => s.setResourceDivert);
  const openEnergyPanel = useStudio((s) => s.openEnergyPanel);
  const typologyId = useStudio((s) => s.buildingTypology);
  const occ = Number(occupancy) || 0;
  const ghi = weather?.ghi || 0;
  const conv = resource.conversion;
  const cat = resource.category || "";
  const blob = `${resource.label || ""} ${resource.output || ""} ${resource.source || ""} ${resource.input || ""} ${resource.tech || ""}`.toLowerCase();
  const isSolar = cat === "Renewable Energy" || /solar|pv|photovolt/.test(blob);
  const isSolarThermal = isSolar && /thermal|hot water/.test(blob);
  const isHeat = !isSolar && /heat|hrv|hvac|exchange|pump|thermal/.test(blob);

  // Solar thermal — direct HEAT from glazed collectors (η ≈ 0.45, no PV performance ratio). Distinct
  // technology from PV: produces hot water/heat, not electricity.
  if (isSolarThermal) {
    const areaM2 = Number(solarThermalAreaM2) || 0;
    const ST_ETA = Number(solarThermalEff) || 0.40;
    const gen = ghi > 0 && areaM2 > 0 ? ghi * 365 * areaM2 * ST_ETA : 0;
    const genLo = Math.round(gen * 0.85), genHi = Math.round(gen * 1.15);
    return (
      <div style={styles.blockBox} data-testid={`resource-quantify-${resource.id}`}>
        <div style={styles.wasteHeatRow}>
          <span style={styles.qLabel}>Collector area</span>
          <input type="number" min="0" step="1" value={solarThermalAreaM2 || ""} placeholder="0" data-testid="solar-thermal-area-input"
            onChange={(e) => setResourceInput("solarThermalAreaM2", Math.max(0, parseFloat(e.target.value) || 0))} style={styles.areaInput} />
          <span style={styles.areaUnit}>m²</span>
        </div>
        <div style={styles.wasteHeatRow}>
          <span style={styles.qLabel}>Collector efficiency η</span>
          <input type="number" min="0.1" max="0.9" step="0.05" value={solarThermalEff ?? ""} placeholder="0.40" data-testid="solar-thermal-eff-input"
            onChange={(e) => setResourceInput("solarThermalEff", e.target.value === "" ? null : Math.max(0.1, Math.min(0.9, parseFloat(e.target.value) || 0)))} style={styles.areaInput} />
          <span style={styles.areaUnit}>η</span>
        </div>
        <div style={styles.qRow}><span style={styles.qLabel}>Expected heat</span><span style={styles.qVal} data-testid={`resource-output-${resource.id}`}>{ghi <= 0 ? "set site climate" : areaM2 <= 0 ? "enter collector area" : `${genLo.toLocaleString()} – ${genHi.toLocaleString()} kWh/year`}</span></div>
        <div style={styles.qRange}>Q = GHI × 365 × area × {ST_ETA.toFixed(2)} η, daylight only. Flat-plate ≈ 0.40 · evacuated-tube ≈ 0.55. Not PV electricity.</div>
        {isEnergyResource(resource.id) && (
          <button style={styles.profileBtn} data-testid={`energy-profile-open-${resource.id}`}
            onClick={(e) => { e.stopPropagation(); openEnergyPanel(resource.id); }}>
            View operational profile (day/night · seasons)
          </button>
        )}
      </div>
    );
  }

  // Solar PV — dynamic expected electricity from the Location ring's GHI × user PV area.
  if (isSolar) {
    const pvA = Number(pvAreaM2) || 0;
    const gen = ghi > 0 && pvA > 0 ? ghi * 365 * pvA * 0.20 * 0.75 : 0;
    const genLo = Math.round(gen * 0.85), genHi = Math.round(gen * 1.15);
    return (
      <div style={styles.blockBox} data-testid={`resource-quantify-${resource.id}`}>
        <div style={styles.wasteHeatRow}>
          <span style={styles.qLabel}>PV / collector area</span>
          <input type="number" min="0" step="1" value={pvAreaM2 || ""} placeholder="0" data-testid="pv-area-input"
            onChange={(e) => setResourceInput("pvAreaM2", Math.max(0, parseFloat(e.target.value) || 0))} style={styles.areaInput} />
          <span style={styles.areaUnit}>m²</span>
        </div>
        <div style={styles.qRow}><span style={styles.qLabel}>Expected generation</span><span style={styles.qVal} data-testid={`resource-output-${resource.id}`}>{ghi <= 0 ? "set site climate" : pvA <= 0 ? "enter PV area" : `${genLo.toLocaleString()} – ${genHi.toLocaleString()} kWh/year`}</span></div>
        <div style={styles.qRange}>E = GHI × 365 × area × 0.20 η × 0.75 PR, from the site&apos;s climate.</div>
        {isEnergyResource(resource.id) && (
          <button style={styles.profileBtn} data-testid={`energy-profile-open-${resource.id}`}
            onClick={(e) => { e.stopPropagation(); openEnergyPanel(resource.id); }}>
            View operational profile (day/night · seasons)
          </button>
        )}
      </div>
    );
  }

  // Thermal exchange — a heat exchanger TRANSFERS heat from a hotter to a colder stream (it does
  // not generate heat). Per-loop config drives recovered kWh via thermalRecovery().
  if (isHeat) {
    return <ThermalConfig resource={resource} />;
  }

  // Valorization / treatment — % of the occupant-driven stream diverted to this loop.
  if (conv && conv.stream) {
    const pct = divertMap?.[resource.id] ?? DEFAULT_DIVERT_PCT;
    const streams = kb?.resource_streams || {};
    const typo = TYPOLOGIES.find((x) => x.id === typologyId);
    const q = quantifyResource(resource, pct, streams, { occupancy: occ, precipMmYr: weather?.precip || 0, organicKgPersonYr: organicKgPersonYr(typologyId) });
    const streamLabel = { organic: "organics", greywater: "greywater", blackwater: "blackwater", rain: "rainwater" }[conv.stream] || conv.stream;
    const streamCite = conv.stream === "organic" ? (typo?.organicRef || "EPA/UN Food Waste Index 2024") : streams[conv.stream]?.ref;
    const perCapNote = conv.stream === "organic"
      ? `${organicKgPersonYr(typologyId)} kg/person\u00b7yr${typo ? ` (${typo.label})` : " (default)"}`
      : (streams[conv.stream] ? `${streams[conv.stream].value} ${streams[conv.stream].unit}` : null);
    const kindLabel = { energy: "energy", water: "treated water", amendment: "amendment/nutrient" }[conv.output_kind] || "output";
    // Clean per-year output unit (fixes the legacy "kg/kg input/yr" label).
    const outputUnit = { energy: "kWh", amendment: "kg", water: "m³", co2: "kg" }[conv.output_kind] || (q?.outputUnit || "");
    const ready = occ > 0 && q && q.generated > 0;
    const round = (v) => Math.round(v).toLocaleString();
    const bm = resource.biogas_model;
    return (
      <div style={styles.blockBox} data-testid={`resource-quantify-${resource.id}`}>
        <div style={styles.qRow}>
          <span style={styles.qLabel}>Available {streamLabel}</span>
          <span style={styles.qVal}>{ready ? `${round(q.generated)} ${q.genUnit}` : "set occupancy to quantify"}</span>
        </div>
        {perCapNote && <div style={styles.qRange} data-testid={`resource-basis-${resource.id}`}>Basis: {perCapNote} × {occ || "—"} occupants</div>}
        <div style={styles.divertRow}>
          <span style={styles.qLabel}>Divert to this loop</span>
          <div style={styles.divertCtrl}>
            <input type="range" min="0" max="100" step="5" value={pct} data-testid={`resource-divert-${resource.id}`}
              onChange={(e) => setResourceDivert(resource.id, e.target.value)} style={styles.divertRange} />
            <input type="number" min="0" max="100" step="5" value={pct} data-testid={`resource-divert-input-${resource.id}`}
              onChange={(e) => setResourceDivert(resource.id, e.target.value)} style={styles.divertNum} />
            <span style={styles.areaUnit}>%</span>
          </div>
        </div>
        {ready && (
          <>
            <div style={styles.qRow}><span style={styles.qLabel}>Diverted input</span><span style={styles.qVal}>{round(q.input)} {q.inputUnit} <span style={styles.qMuted}>(× {(conv.capture_eff ?? 1)} capture)</span></span></div>
            <div style={styles.qRow}><span style={styles.qLabel}>Valorized {kindLabel}</span><span style={{ ...styles.qVal, color: colors.ok }} data-testid={`resource-output-${resource.id}`}>{round(q.outputQty)} {outputUnit}/yr</span></div>
            {q.areaModel && q.areaModel.model !== "zero" && q.spaceM2 != null && (
              <>
                <div style={styles.qRow}>
                  <span style={styles.qLabel}>
                    Reference area required
                    <span style={{ marginLeft: 5, display: "inline-flex", verticalAlign: "middle" }}>
                      <InfoTip testId={`resource-area-info-${resource.id}`} accent={colors.textTertiary} placement="top"
                        title={`${q.areaModel.process || "Equipment"} footprint — how it scales`}
                        body={areaTooltip(q.areaModel)} />
                    </span>
                  </span>
                  <span style={{ ...styles.qVal, fontWeight: 700 }} data-testid={`resource-area-${resource.id}`}>~{round(q.spaceM2)} m²</span>
                </div>
                <div style={styles.qRange} data-testid={`resource-area-detail-${resource.id}`}>
                  {q.areaIntensity != null ? `${q.areaIntensity.toFixed(2)} m² per ${q.areaModel.throughput_basis || "unit/day"}` : ""}
                  {q.marginalArea != null ? ` · next 10% adds ~${q.marginalArea < 0.05 ? "0" : q.marginalArea.toFixed(1)} m²` : ""}
                  {q.parasiticKwh != null ? ` · ~${round(q.parasiticKwh)} kWh/yr to run` : ""}
                </div>
                <div style={styles.qMuted} data-testid={`resource-area-note-${resource.id}`}>
                  Area scales with how much you divert — informational only (not deducted from growing area yet). {q.areaModel.placement ? `Placed on: ${q.areaModel.placement}.` : ""}
                </div>
              </>
            )}
            {!q.areaModel && q.units != null && (
              <div style={styles.qRange} data-testid={`resource-equipment-${resource.id}`}>
                Implied equipment: ~{q.units} × {conv.input_capacity} {conv.input_unit} unit{q.units === 1 ? "" : "s"}
                {q.spaceM2 != null ? ` · ~${round(q.spaceM2)} m² footprint` : ""}
                {q.parasiticKwh != null ? ` · ~${round(q.parasiticKwh)} kWh/yr to run` : ""}
              </div>
            )}
            {bm && <div style={styles.qRange}>Energy basis: {bm.usable_kwh_per_kg} kWh/kg usable · {bm.thermal_kwh_per_kg} kWh/kg thermal (VS model: {bm.vs_fraction} VS × {bm.ch4_m3_per_kgvs} m³CH₄/kg × {bm.kwh_per_m3_ch4} kWh/m³)</div>}
          </>
        )}
        <div style={styles.qCite}>
          <span style={styles.qMuted}>Sources:</span> {conv.ref ? <CiteTag cite={conv.ref} /> : <span style={styles.bUserDef} title="No high-impact reference for this conversion; user-defined estimate.">user-defined</span>} {streamCite && <CiteTag cite={streamCite} />}
        </div>
        {isEnergyResource(resource.id) && (
          <button style={styles.profileBtn} data-testid={`energy-profile-open-${resource.id}`}
            onClick={(e) => { e.stopPropagation(); openEnergyPanel(resource.id); }}>
            View operational profile (day/night · seasons)
          </button>
        )}
      </div>
    );
  }

  return null;
}

// Compact headline for a LOCKED resource loop (shown in the collapsed summary).
export function ResourceLockedSummary({ resource }) {
  const divertMap = useStudio((s) => s.resourceInputs.divert);
  const pvAreaM2 = useStudio((s) => s.resourceInputs.pvAreaM2);
  const solarThermalAreaM2 = useStudio((s) => s.resourceInputs.solarThermalAreaM2);
  const thermalCfg = useStudio((s) => (s.resourceInputs.thermal || {})[resource.id]) || {};
  const conv = resource.conversion;
  const cat = resource.category || "";
  const blob = `${resource.label || ""} ${resource.output || ""} ${resource.tech || ""}`.toLowerCase();
  const isSolar = cat === "Renewable Energy" || /solar|pv|photovolt/.test(blob);
  const isSolarThermal = isSolar && /thermal|hot water/.test(blob);
  const isHeat = !isSolar && /heat|hrv|hvac|exchange|pump|thermal/.test(blob);
  let line;
  if (isSolarThermal) line = `Collector area ${Number(solarThermalAreaM2) || 0} m² (thermal)`;
  else if (isSolar) line = `PV area ${Number(pvAreaM2) || 0} m²`;
  else if (isHeat) line = `Available heat ${Number(thermalCfg.availableKw) || 0} kW · ${isCondenserLoop(resource) ? (thermalCfg.condenserType === "water" ? "water-cooled" : "air-cooled") : (RECOVERY_METHODS.find((m) => m.id === (thermalCfg.method || "plate")) || {}).label || "recovery"}`;
  else if (conv?.stream) line = `Diverting ${divertMap?.[resource.id] ?? DEFAULT_DIVERT_PCT}% of ${conv.stream}`;
  else line = "Loop configured";
  return <span style={styles.resLockedLine} data-testid={`resource-locked-summary-${resource.id}`}>{line}</span>;
}

// A single resource option with the "Done · lock in" flow. Only LOCKED loops feed the circularity
// balance & Resources ring; a selected-but-unlocked loop shows its editor + a Done button.
export function ResourceOption({ r, active, toggle, relTags }) {
  const locked = useStudio((s) => !!(s.resourceInputs.locked || {})[r.id]);
  const setResourceLocked = useStudio((s) => s.setResourceLocked);
  return (
    <div>
      <OptionRow accent={PILLAR_BY_ID.resources.solid} active={active} title={r.label}
        desc={r.output ? `${r.output}${r.typical_yield ? ` · ${r.typical_yield} ${r.recovered_unit || ""}/yr` : ""}` : r.category}
        testID={`opt-resource-${r.id}`} onClick={() => toggle("resources", r.id)}
        icon={resourceIcon(r)} tip={getOptTip("resources", r)}
        recommended={relTags.resources[r.id]?.recommended} recReason={relTags.resources[r.id]?.reason} />
      {active && !locked && (
        <>
          <ResourceQuantify resource={r} />
          <div style={styles.resLockRow}>
            <button data-testid={`resource-done-${r.id}`} style={styles.resDoneBtn}
              onClick={(e) => { e.stopPropagation(); setResourceLocked(r.id, true); }}>Done · lock in</button>
            <span style={styles.resLockHint}>Only locked loops count toward circularity.</span>
          </div>
        </>
      )}
      {active && locked && (
        <div style={styles.resLockedBox} data-testid={`resource-locked-${r.id}`}>
          <div style={styles.resLockedTop}>
            <span style={styles.resLockedBadge}>✓ Locked in</span>
            <button data-testid={`resource-edit-${r.id}`} style={styles.resEditBtn}
              onClick={(e) => { e.stopPropagation(); setResourceLocked(r.id, false); }}>Edit</button>
          </div>
          <ResourceLockedSummary resource={r} />
        </div>
      )}
    </div>
  );
}
